import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import SuperAdminLayout from '../components/admin/SuperAdminLayout';
import { adminService } from '../services/api';

const SuperAdminUsers = () => {
    const [userData, setUserData] = useState([]);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await adminService.getDashboardStats();
                if (response.data && response.data.success && response.data.data.users) {
                    const transformedUsers = response.data.data.users.map(u => ({
                        id: u.id,
                        name: u.name,
                        email: u.email,
                        phone: '-', // No phone in user table by default
                        role: u.role || 'user',
                        status: 'Aktif',
                        statusColor: 'bg-[#0DBC94]'
                    }));
                    setUserData(transformedUsers);
                }
            } catch (error) {
                console.error("Failed to fetch dashboard stats", error);
            }
        };
        fetchStats();
    }, []);

    // CRUD Logic
    const [isEditUserOpen, setIsEditUserOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', role: 'Admin', status: 'Aktif' });

    const handleEditClick = (user) => {
        setCurrentUser(user);
        setFormData({ name: user.name, email: user.email, phone: user.phone, role: user.role, status: user.status });
        setIsEditUserOpen(true);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            await adminService.updateUser(currentUser.id, formData);
            const updatedUsers = userData.map(user =>
                user.id === currentUser.id
                    ? { ...user, ...formData, statusColor: formData.status === 'Aktif' ? 'bg-[#0DBC94]' : 'bg-[#F64E60]' }
                    : user
            );
            setUserData(updatedUsers);
            setIsEditUserOpen(false);
        } catch (error) {
            console.error("Failed to update user", error);
        }
    };

    const handleDeleteClick = (user) => {
        setCurrentUser(user);
        setIsDeleteConfirmOpen(true);
    };

    const handleDeleteUser = async () => {
        try {
            await adminService.deleteUser(currentUser.id);
            const remainingUsers = userData.filter(user => user.id !== currentUser.id);
            setUserData(remainingUsers);
            setIsDeleteConfirmOpen(false);
        } catch (error) {
            console.error("Failed to delete user", error);
        }
    };

    return (
        <SuperAdminLayout activePage="users" title="User">
            {/* Total User Table */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-8">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800 leading-none">Total User</h3>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-l-xl">Nama</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">No. Telepon</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role User</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-r-xl">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {userData.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors group">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-100 rounded-full flex-shrink-0"></div>
                                            <span className="font-bold text-sm text-gray-700">{user.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-sm font-medium text-gray-500">{user.email}</td>
                                    <td className="px-6 py-5 text-sm font-medium text-gray-500">{user.phone}</td>
                                    <td className="px-6 py-5 text-sm font-medium text-gray-500">{user.role}</td>
                                    <td className="px-6 py-5">
                                        <div className={`flex items-center justify-center w-[110px] h-8 ${user.statusColor} text-white text-[10px] font-black rounded-lg`}>
                                            {user.status}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEditClick(user)}
                                                className="p-2 text-gray-400 hover:text-[#06B6D4] hover:bg-[#E0F7FA] rounded-lg transition-colors"
                                            >
                                                <Icon icon="material-symbols:edit-outline" className="text-xl" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(user)}
                                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <Icon icon="material-symbols:delete-outline" className="text-xl" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit User Modal */}
            {isEditUserOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Edit User</h3>
                            <button onClick={() => setIsEditUserOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <Icon icon="material-symbols:close" className="text-2xl" />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateUser} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Nama</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#06B6D4] focus:ring-1 focus:ring-[#06B6D4] transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#06B6D4] focus:ring-1 focus:ring-[#06B6D4] transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">No. Telepon</label>
                                <input
                                    type="text"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#06B6D4] focus:ring-1 focus:ring-[#06B6D4] transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#06B6D4] focus:ring-1 focus:ring-[#06B6D4] transition-all"
                                >
                                    <option value="Aktif">Aktif</option>
                                    <option value="Non-Aktif">Non-Aktif</option>
                                </select>
                            </div>
                            <button type="submit" className="w-full py-3 bg-[#06B6D4] text-white font-bold rounded-xl hover:bg-[#0891b2] transition-colors mt-4">
                                Simpan Perubahan
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteConfirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl animate-fade-in text-center">
                        <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Icon icon="material-symbols:warning-outline" className="text-3xl" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Hapus User?</h3>
                        <p className="text-gray-500 mb-8">Apakah anda yakin ingin menghapus user <strong>{currentUser?.name}</strong>? Tindakan ini tidak dapat dibatalkan.</p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setIsDeleteConfirmOpen(false)}
                                className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDeleteUser}
                                className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-colors"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </SuperAdminLayout>
    );
};

export default SuperAdminUsers;
